// 내 버츄펫 도메인 엔티티 — 펫 상태(바이탈)와 행동(먹이기 등) 응집.
//  - 상태값: hunger/energy/mood/awake (+ 각 max)
//  - 행동: feed() — 서버 RPC(원자적: 인벤토리 차감 + 회복) 호출 후 상태 동기화
//  - 도메인 규칙(순수): applyFeed() — 음식 효과를 상한 클램프해 상태에 반영(서버 규칙의 클라 미러)
// 서버(feed_pet RPC)가 권위(authoritative). 엔티티는 클라 측 단일 진입점/상태 소유자.
import SupabaseApi from "@/api/SupabaseApi";
import PetApi from "@/api/PetApi";

const MAX = 100;

export default class MyPet {
  constructor({ petId = null, name = "", modelIndex = null, meters = {} } = {}) {
    this.petId = petId;
    this.name = name;
    this.modelIndex = modelIndex;

    // 상태값 (응집)
    this.hunger = meters.hunger ?? 0;
    this.hungerMax = meters.hungerMax ?? MAX;
    this.energy = meters.energy ?? 0;
    this.energyMax = meters.energyMax ?? MAX;
    this.mood = meters.mood ?? 0;
    this.moodMax = meters.moodMax ?? MAX;
    this.awake = meters.awake ?? 0;
    this.awakeMax = meters.awakeMax ?? MAX;
  }

  // 활성 펫의 현재 바이탈을 불러와 엔티티 생성 (factory)
  static async load() {
    try {
      const meters = await SupabaseApi.PetStatus.fetchMyMeters();
      return new MyPet({ meters });
    } catch (e) {
      console.error("MyPet.load failed", e);
      return new MyPet();
    }
  }

  // 서버 응답(바이탈)으로 상태 동기화
  syncMeters(meters = {}) {
    if (typeof meters.hunger === "number") this.hunger = meters.hunger;
    if (typeof meters.energy === "number") this.energy = meters.energy;
    if (typeof meters.mood === "number") this.mood = meters.mood;
    if (typeof meters.awake === "number") this.awake = meters.awake;
    return this;
  }

  // ───── 도메인 규칙(순수) ─────
  // 음식 효과를 상태에 적용(상한 클램프). 영속 없음 — 계산/낙관적 UI/테스트용.
  applyFeed(food, qty = 1) {
    this.hunger = Math.min(this.hungerMax, this.hunger + (food.hunger || 0) * qty);
    this.energy = Math.min(this.energyMax, this.energy + (food.energy || 0) * qty);
    return this;
  }

  // ───── 행동(영속) ─────
  // 밥주기: 음식 검증 → 펫 Edge Function(pet/feed: 인벤토리 차감 + 회복 + favorite luck 보너스)
  //  → 반환 수치로 상태 동기화. 반환: { hunger, energy, qtyLeft, favorite, bonus }
  async feed(food, qty = 1) {
    if (!food || !food.id) throw new Error("E_NO_FOOD");
    if (food.type && food.type !== "FOOD") throw new Error("E_NOT_FOOD");
    const result = await PetApi.feed(food.id, qty);
    this.syncMeters(result); // 서버 권위 값으로 동기화
    return result;
  }

  // 놀아주기: 장난감 내구도 소모 + mood 회복(+favorite luck 보너스). Edge(pet/play) → play_pet RPC.
  //  반환: { mood, usesLeft, broke, favorite, bonus }
  async play(toy) {
    if (!toy || !toy.id) throw new Error("E_NO_TOY");
    if (toy.type && toy.type !== "TOY") throw new Error("E_NOT_TOY");
    const result = await PetApi.play(toy.id);
    this.syncMeters(result); // mood 동기화
    return result;
  }

  // ───── 파생 상태(표시/판정용) ─────
  pct(meter) {
    const max = this[`${meter}Max`] || MAX;
    return max ? Math.round((this[meter] / max) * 100) : 0;
  }
  get hungerPct() {
    return this.pct("hunger");
  }
  get energyPct() {
    return this.pct("energy");
  }
  isHungry() {
    return this.hungerPct < 30;
  }
  isTired() {
    return this.energyPct < 30;
  }
}
